/** 应用实例创建/编辑：UI 阶段仅维护 JSON 字段，提交时再组装组 spec */
import type { FormInstance } from '@arco-design/web-react/es/Form';
import { cloneDeep } from 'lodash';

import {
  applyRegistryToGroups,
  mergeRegistryPayload,
  placeRegistryOnGroupSystem,
  readGroupSystemRegistry,
  readRegistryPayloadFromForm,
  readRegistryPayloadFromValues,
} from '@/delivery-type-plugin/capabilities/image';
import type { MaasSubmitError } from '@/delivery-type-plugin/capabilities/maas-init';
import { MAAS_INFERENCE_SOURCE_FIELD, MaasMode, MaasRunMode, normalizeMaasRuntime } from '@/delivery-type-plugin/capabilities/maas-init';
import { parseFormJsonField } from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';
import {
  INFERENCE_SERVICE_FIELD,
  INFERENCE_SYSTEM_FIELD,
  MASTER_NODES_FIELD,
  MASTER_SYSTEM_DISK_FIELD,
  WORKER_NODES_FIELD,
  WORKER_SYSTEM_DISK_FIELD,
  assembleGroupSpecsFromNodeFields,
  collectBaselineNodesFromInitValues,
  ensureFrameworkForSubmit,
  stripGroupInitCommand,
  validateMaasGroupedGpuUsageLatest,
} from '@/delivery-type-plugin/capabilities/maas-node-groups';
import {
  applyContainerDeployConfigToGroupedSpec,
  resolveContainerDeployConfigFromFormValues,
} from '@/delivery-type-plugin/capabilities/maas-system-config';
import {
  SERVICE_MOUNT_GROUP_NAME_FIELD,
  buildServiceMountNodeOptions,
  normalizeServiceList,
  normalizeServiceMountGroupNames,
  serializeServiceListForApi,
} from '@/delivery-type-plugin/capabilities/service';
import {
  getPrimaryGroupFromValues,
  getVolumeClaimName,
  hasMaasInferenceSystemImage,
  isCreateDataVolume,
  isCreateDataVolumeConfigured,
  isExistingDataVolume,
  readMaasFormValues,
  resolveApiRunModeForSubmit,
  resolveFrameworkFromInferenceImage,
  resolveGroupSpecsForSubmit,
  validateMaasGroupedResourcesForSubmit,
  validateMaasRunModeTopology,
  validateMaasSystemDiskForSubmit,
} from '@/delivery-type-plugin/plugins';
import { getUserInfo } from '@/utils/useLocalStorage';

import type { ApplicationInstanceFormValues, ApplicationInstanceSpec } from '../types';
import { DEFAULT_APP_DATA_MOUNT_PATH } from '../types';
import {
  type AppStorageGroup,
  attachAppStorageVolumesForSubmit,
  groupHasConfiguredDataDisk,
  parseAppStorageGroupsFromValues,
  requiresDataDiskConfiguration,
  resolveAppDataMountPath,
  resolveFormSystemVolumeNames,
} from './appStorageGroups';
import { confirmApplicationImageOnForm } from './imageConfirm';
import { buildAppInstanceMaasFormAnnotations } from './maasFormAnnotations';

export { resolveAppDataMountPath } from './appStorageGroups';

/** 确认页 Form.useWatch 字段：与 readApplicationInstanceFormValues 读取范围一致 */
export const APP_INSTANCE_FORM_WATCH_FIELDS = [
  'alias',
  'description',
  'workspace',
  'namespace',
  'runtime',
  'runtime.type',
  'runtime.workload',
  'dataMountPath',
  'framework',
  'runMode',
  MAAS_INFERENCE_SOURCE_FIELD,
  'commonEnv',
  MASTER_NODES_FIELD,
  WORKER_NODES_FIELD,
  INFERENCE_SYSTEM_FIELD,
  INFERENCE_SERVICE_FIELD,
  MASTER_SYSTEM_DISK_FIELD,
  WORKER_SYSTEM_DISK_FIELD,
  SERVICE_MOUNT_GROUP_NAME_FIELD,
  '_appStorageGroups',
] as const;

/** 顶层 network.service：仅读取表单 `_inferenceService`，并注入入口节点 groupNames。 */
function resolveTopLevelNetworkService(values: ApplicationInstanceFormValues) {
  const raw = values._inferenceService;
  if (raw === undefined || raw === null || raw === '') return undefined;
  const list = parseFormJsonField(raw);
  if (!Array.isArray(list) || !list.length) return undefined;
  const serialized = serializeServiceListForApi(list as Parameters<typeof serializeServiceListForApi>[0]);
  if (!serialized?.length) return undefined;
  const mountGroupNames = normalizeServiceMountGroupNames((values as Record<string, unknown>)[SERVICE_MOUNT_GROUP_NAME_FIELD]);
  if (!mountGroupNames.length) return serialized;
  return serialized.map((item) => ({ ...item, groupNames: mountGroupNames }));
}

/** 读取表单 UI 字段（不组装组 spec） */
export function readApplicationInstanceFormValues(form: FormInstance): ApplicationInstanceFormValues {
  const values = readMaasFormValues(form) as ApplicationInstanceFormValues;
  const registry = readRegistryPayloadFromForm(form);
  return {
    ...values,
    ...(registry ? { registry } : {}),
    dataMountPath: (form.getFieldValue('dataMountPath') as string | undefined) || values.dataMountPath || DEFAULT_APP_DATA_MOUNT_PATH,
  };
}

export function formToPayload(values: ApplicationInstanceFormValues): ApplicationInstanceSpec {
  const runtimeType = values.runtime?.type;
  const runMode = values.runMode || MaasRunMode.Normal;
  const mountPath = resolveAppDataMountPath(values);
  const formsystem = resolveFormSystemVolumeNames(parseAppStorageGroupsFromValues(values));
  const assembled = ensureFrameworkForSubmit(
    assembleGroupSpecsFromNodeFields(cloneDeep(values), values as Record<string, unknown>, {
      elevateSharedEnvAndService: true,
    }),
  );
  const submitValues = attachAppStorageVolumesForSubmit(assembled);

  const { headGroupSpec: rawHead, workerGroupSpecs: rawWorkers } = resolveGroupSpecsForSubmit(
    submitValues as Parameters<typeof resolveGroupSpecsForSubmit>[0],
    runtimeType,
  );
  const formRegistry = readRegistryPayloadFromValues(values as Record<string, unknown>);
  const groupRegistry = readGroupSystemRegistry(rawHead) ?? readGroupSystemRegistry(rawWorkers?.[0]);
  const registry = mergeRegistryPayload(formRegistry, groupRegistry);
  const headGroupSpec = rawHead ? stripGroupInitCommand(placeRegistryOnGroupSystem(rawHead, registry)) : undefined;
  const workerGroupSpecs = applyRegistryToGroups(
    rawWorkers.map((group) => stripGroupInitCommand(group)),
    registry,
  );

  applyContainerDeployConfigToGroupedSpec(
    { headGroupSpec, workerGroupSpecs },
    resolveContainerDeployConfigFromFormValues(submitValues as Parameters<typeof resolveContainerDeployConfigFromFormValues>[0]),
  );

  const apiRunMode = resolveApiRunModeForSubmit({
    runMode,
    headGroupSpec,
    workerGroupSpecs,
  });

  const primarySystem = getPrimaryGroupFromValues({ headGroupSpec, workerGroupSpecs, runMode }, runMode)?.system as
    | Record<string, unknown>
    | undefined;

  const { _primaryDataVolume: _ignoredPrimaryDataVolume, ...annotationFormValues } = values;

  const replicas = typeof values.replicas === 'number' && values.replicas > 0 ? values.replicas : 1;
  const networkService = resolveTopLevelNetworkService(values);

  return {
    alias: values.alias,
    description: values.description,
    workspace: values.workspace,
    creator: values.creator || getUserInfo()?.name || '',
    mode: values.mode || MaasMode.Provisioned,
    framework: values.framework || resolveFrameworkFromInferenceImage(null, primarySystem),
    runMode: apiRunMode,
    runtime: normalizeMaasRuntime(values.runtime) || values.runtime,
    replicas,
    network: {
      ...(values.network?.vpc ? { vpc: values.network.vpc } : {}),
      ...(values.network?.securitygroup ? { securitygroup: values.network.securitygroup } : {}),
      ...(networkService?.length ? { service: networkService } : {}),
    },
    headGroupSpec,
    workerGroupSpecs,
    annotations: buildAppInstanceMaasFormAnnotations({
      ...annotationFormValues,
      dataMountPath: mountPath,
      formsystem,
    }),
  };
}

/** @returns i18n key 与可选 field key，通过时返回 undefined */
async function getApplicationInstanceSubmitError(
  values: ApplicationInstanceFormValues,
  spec: ApplicationInstanceSpec,
  options?: { initValues?: ApplicationInstanceFormValues },
): Promise<MaasSubmitError | undefined> {
  if (!values.workspace?.trim()) return { key: 'common:validate.required.select', fieldKey: 'common:tenant.space' };
  if (!values.alias?.trim()) return { key: 'common:validate.required', fieldKey: 'common:name' };
  if (!values.runtime?.type || !values.runtime?.workload) {
    return { key: 'common:validate.required.select', fieldKey: 'data-center:maas.runtime.type' };
  }

  const storageError = getAppStorageSubmitError(values);
  if (storageError) return storageError;

  if (!spec.framework || !spec.runMode) {
    return { key: 'common:validate.required', fieldKey: 'data-center:maas.inference.framework' };
  }

  const topologyError = validateMaasRunModeTopology(spec);
  if (topologyError) return topologyError;

  const systemDiskError = validateMaasSystemDiskForSubmit(values);
  if (systemDiskError) return systemDiskError;

  if (!hasMaasInferenceSystemImage(values)) {
    return { key: 'data-center:maas.app.image.required' };
  }

  const services = normalizeServiceList(parseFormJsonField(values._inferenceService));
  if (services.length) {
    const mountOptions = buildServiceMountNodeOptions(values);
    const mountGroupNames = normalizeServiceMountGroupNames((values as Record<string, unknown>)[SERVICE_MOUNT_GROUP_NAME_FIELD]);
    if (mountOptions.length && !mountGroupNames.length) {
      return { key: 'common:validate.required.select', fieldKey: 'data-center:maas.service.entry.nodes' };
    }
  }

  const resourceError = validateMaasGroupedResourcesForSubmit(spec);
  if (resourceError) return resourceError;

  const baselineNodes = options?.initValues ? collectBaselineNodesFromInitValues(options.initValues) : undefined;
  return validateMaasGroupedGpuUsageLatest(spec, { baselineNodes });
}

/** 仅从表单读取并组装 spec，不请求 resource-configurations（订单 metadata 等场景） */
export function readApplicationInstanceSpecFromForm(
  form: FormInstance,
  options?: { overlay?: Partial<ApplicationInstanceFormValues> },
): { values: ApplicationInstanceFormValues; spec: ApplicationInstanceSpec } {
  const values = {
    ...readApplicationInstanceFormValues(form),
    ...options?.overlay,
  };
  return { values, spec: formToPayload(values) };
}

/** 读取表单、组装 spec、执行业务校验（提交入口单次调用） */
export async function prepareApplicationInstanceSubmit(
  form: FormInstance,
  options?: {
    overlay?: Partial<ApplicationInstanceFormValues>;
    initValues?: ApplicationInstanceFormValues;
    t?: (key: string, params?: Record<string, unknown>) => string;
  },
): Promise<{
  values: ApplicationInstanceFormValues;
  spec: ApplicationInstanceSpec;
  submitError?: MaasSubmitError;
}> {
  if (options?.t) {
    const confirmed = await confirmApplicationImageOnForm(form, options.t);
    if (confirmed.ok === false) {
      const values = {
        ...readApplicationInstanceFormValues(form),
        ...options?.overlay,
      };
      return { values, spec: formToPayload(values), submitError: { key: confirmed.message, alreadyNotified: true } };
    }
  }
  const values = {
    ...readApplicationInstanceFormValues(form),
    ...options?.overlay,
  };
  const spec = formToPayload(values);
  const submitError = await getApplicationInstanceSubmitError(values, spec, { initValues: options?.initValues });
  return { values, spec, submitError };
}

function validateConfiguredDataVolume(group: AppStorageGroup): boolean {
  if (!groupHasConfiguredDataDisk(group)) return false;
  const data = group.volume!;
  if (!data.reclaimPolicy) return false;
  if (!String(group.mountPath || '').trim()) return false;
  if (isExistingDataVolume(data)) {
    return !!getVolumeClaimName(data);
  }
  if (isCreateDataVolume(data)) {
    return isCreateDataVolumeConfigured(data);
  }
  return true;
}

/** 临时卷仅校验挂载路径；持久卷需配置完整数据盘 */
export function getAppStorageSubmitError(values: ApplicationInstanceFormValues): MaasSubmitError | undefined {
  const groups = parseAppStorageGroupsFromValues(values);
  for (const group of groups) {
    if (!String(group.mountPath || '').trim()) {
      return { key: 'common:validate.required', fieldKey: 'data-center:maas.model.mountPath' };
    }
    if (requiresDataDiskConfiguration(group)) {
      if (!groupHasConfiguredDataDisk(group) || !validateConfiguredDataVolume(group)) {
        return { key: 'data-center:maas.storage.configure.first' };
      }
    }
  }
  return undefined;
}

/** @deprecated 使用 getAppStorageSubmitError */
export function validateAppStorage(values: ApplicationInstanceFormValues): boolean {
  return !getAppStorageSubmitError(values);
}
